import { useSelector } from 'react-redux';
import { ENVIRONMENT, Nullable } from '../types/general';
import { useQueryParams } from './useQueryParams';
import { apiConfigSelector } from '../redux/reducers/apiConfigReducer';
import {
  EventActionValues,
  EventCategoryValues,
  EventContextValues,
  EventLabelValues,
  TrackingProperties,
  VIEWS,
} from '../types/tracking';
import { ApiConfig } from '../types/apiConfig';
import { contextSelector } from '../redux/reducers/contextReducer';
import { useCallback } from 'react';
import { useBrandGroupConfig } from './useBrandGroupConfig';
import { useRegistrationFlowConfiguration } from './useRegistrationFlowConfiguration';
import { IdentityScreenAction } from '../types/registrationFlows';
import {
  CIAM_LOCAL_STORAGE_KEYS,
  getLocalStorageValue,
  setLocalStorageValue,
} from '../utils/localstorage';
import { v4 as uuid } from 'uuid';
import { getEnvironment } from '../utils/configUtils';

export function useTracking() {
  const apiConfig = useSelector(apiConfigSelector);
  const { view, eventCategory } = useSelector(contextSelector);
  const { identityLevel } = useRegistrationFlowConfiguration();
  const { brand, articleId, utmInternal, cookieId, sessionId } = useQueryParams();
  const { getBrandGroupConfigFromBrandId } = useBrandGroupConfig();

  const pushEventToDataLayer = useCallback(
    (trackingData: TrackingProperties) => {
      try {
        const dataLayer = window.dataLayer || [];
        const brandGroupConfig = getBrandGroupConfigFromBrandId(brand);

        const event = {
          ...trackingData,
          ...getGeneralProps(apiConfig),
          brandcode: brand || null,
          brandgroup: brandGroupConfig?.id || null,
          articleid: articleId || null,
          cookieid: cookieId || getCookieId() || null,
          utminternal: utmInternal || null,
          sessionid: sessionId || null,
          itemdescription:
            trackingData.itemdescription !== undefined
              ? trackingData.itemdescription
              : identityLevel?.toLowerCase(),
          ga_event_name: trackingData.ga_event_name || null,
        };

        dataLayer.push(event);
      } catch (error) {
        // Silently handle the error to prevent blocking
        console.warn('Failed to push event to data layer:', error);
      }
    },
    [
      getBrandGroupConfigFromBrandId,
      brand,
      apiConfig,
      articleId,
      cookieId,
      utmInternal,
      sessionId,
      identityLevel,
    ]
  );

  const trackEventWithCategory = useCallback(
    (
      eventAction: EventActionValues,
      eventLabel: Nullable<EventLabelValues>,
      eventCategory: Nullable<EventCategoryValues>,
      pageTitle?: string,
      itemDescription?: string,
      gaEventName?: string
    ) => {
      try {
        const trackingProperties: TrackingProperties = {
          eventaction: eventAction,
          eventcategory: eventCategory,
          eventlabel: eventLabel,
          pagetitle: pageTitle,
          itemdescription: itemDescription,
          ga_event_name: gaEventName,
        };
        pushEventToDataLayer(trackingProperties);
      } catch (error) {
        // Silently handle the error to prevent blocking
        console.warn('Failed to track event with category:', error);
      }
    },
    [pushEventToDataLayer]
  );

  const trackEvent = useCallback(
    (eventAction: EventActionValues, eventLabel: Nullable<EventLabelValues>) => {
      try {
        trackEventWithCategory(eventAction, eventLabel, eventCategory ?? null);
      } catch (error) {
        // Silently handle the error to prevent blocking
        console.warn('Failed to track event:', error);
      }
    },
    [trackEventWithCategory, eventCategory]
  );

  const trackEventWithContext = useCallback(
    (eventAction: EventActionValues, eventContext: EventContextValues) => {
      try {
        if (!view) {
          return;
        }
        const eventLabel = getEventLabel(eventContext, view);
        trackEvent(eventAction, eventLabel);
      } catch (error) {
        // Silently handle the error to prevent blocking
        console.warn('Failed to track event with context:', error);
      }
    },
    [view, trackEvent]
  );

  return {
    trackEventWithCategory,
    trackEvent,
    trackEventWithContext,
  };
}

function getEventLabel(eventContext: EventContextValues, view: VIEWS): EventLabelValues | null {
  switch (eventContext) {
    case EventContextValues.Cancel:
      switch (view) {
        case VIEWS.CONFIRM_EMAIL:
          return EventLabelValues.ConfirmEmailCancel;
        case VIEWS.PERSONAL_INFO:
          return EventLabelValues.PersonalInfoCancel;
        case VIEWS.ACCOUNT_CONSENT:
          return EventLabelValues.ProvideCredentialsCancel;
      }
      break;
    case EventContextValues.ReadMore:
      switch (view) {
        case VIEWS.CONFIRM_EMAIL:
          return EventLabelValues.ConfirmEmailReadMore;
        case VIEWS.PERSONAL_INFO:
          return EventLabelValues.PersonalInfoReadMore;
        case VIEWS.ACCOUNT_CONSENT:
          return EventLabelValues.ProvideCredentialsReadMore;
      }
      break;
  }
  return null;
}

function getGeneralProps(apiConfig: ApiConfig) {
  const env = getEnvironment(apiConfig.fusionAuth.domain as ENVIRONMENT);

  // Build up GTM event
  const generalPropertyData = {
    application: 'www',
    disableShortnames: true,
    environment: env,
    eventid: null,
    identyaccountid: null,
    identityaccounttype: null,
    isnewdataformat: true,
    sessionid: null,
    sharedsdkversion: null,
    subscriptionid: null,
    testmode: false,
    viewid: null,
    event: 'user_action',
  };

  const consentProperties = {
    consentcookies: false,
    consentcreateadsprofile: false,
    consentcreatecontentprofile: false,
    consentimproveproducts: false,
    consentmarketresearch: false,
    consentmeasureadperformance: false,
    consentmeasurecontentperformance: false,
    consentselectbasicads: false,
    consentselectpersonalizedads: false,
    consentselectpersonalizedcontent: false,
    consentsocialmedia: false,
  };

  const wallPropertyData = {
    wallid: null,
  };

  return {
    ...generalPropertyData,
    ...consentProperties,
    ...wallPropertyData,
  };
}

export function getEventCategory(action: IdentityScreenAction) {
  switch (action) {
    case IdentityScreenAction.REGISTER:
      return EventCategoryValues.Registration;
    case IdentityScreenAction.UPGRADE:
    case IdentityScreenAction.LOGIN:
      return EventCategoryValues.Login;
    default:
      return null;
  }
}

function getCookieId() {
  const cookieId = getLocalStorageValue(CIAM_LOCAL_STORAGE_KEYS.COOKIE_ID);
  if (cookieId) {
    return cookieId;
  }
  const newCookieId = uuid();
  setLocalStorageValue(CIAM_LOCAL_STORAGE_KEYS.COOKIE_ID, newCookieId);
  return newCookieId;
}
